import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Search, CreditCard, Banknote, CheckSquare } from 'lucide-react';
import api from '../api';

const fmtCurrency = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

const PAYMENT_TYPES = [
  { key: 'cash', label: 'Cash', icon: Banknote, color: 'bg-green-100 text-green-700' },
  { key: 'check', label: 'Check', icon: CheckSquare, color: 'bg-blue-100 text-blue-700' },
  { key: 'square', label: 'Square Credit Card', icon: CreditCard, color: 'bg-purple-100 text-purple-700' },
];

const emptyPayment = { customer_name: '', sales_order_number: '', payment_type: 'cash', amount: '', check_number: '', check_date: '', check_front_url: '', check_back_url: '', square_transaction_id: '', notes: '' };

export default function PaymentsReceived() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyPayment });
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [squareTxns, setSquareTxns] = useState([]);
  const [orderInfo, setOrderInfo] = useState(null);  // looked-up SO {invoice_total, balance_due, ...}
  const [orderLookupErr, setOrderLookupErr] = useState('');
  const [orderLookupLoading, setOrderLookupLoading] = useState(false);
  // R11: SO# typeahead
  const [soSuggestions, setSoSuggestions] = useState([]);
  const [showSoSuggestions, setShowSoSuggestions] = useState(false);

  // Debounced search for SO# typeahead — fires on each keystroke after 150ms
  useEffect(() => {
    const q = (form.sales_order_number || '').trim();
    if (!q || orderInfo) { setSoSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const orders = await api.request(`/api/sales-orders?search=${encodeURIComponent(q)}&limit=8`);
        setSoSuggestions((orders || []).slice(0, 8));
      } catch {
        setSoSuggestions([]);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [form.sales_order_number, orderInfo]);

  const lookupOrder = async (orderNum) => {
    const trimmed = (orderNum || '').trim();
    setOrderInfo(null);
    setOrderLookupErr('');
    if (!trimmed) return;
    setOrderLookupLoading(true);
    try {
      const data = await api.request(`/api/payments/lookup-order/${encodeURIComponent(trimmed)}`);
      setOrderInfo(data);
      // Auto-fill customer if not yet set
      setForm(prev => ({
        ...prev,
        customer_name: prev.customer_name || data.customer_name || '',
      }));
    } catch (e) {
      setOrderLookupErr(e.message || 'Sales order not found');
    } finally {
      setOrderLookupLoading(false);
    }
  };

  const loadPayments = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?payment_type=${filter}` : '';
      const data = await api.request(`/api/payments${params}`);
      setPayments(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadCustomers = async () => {
    try { const data = await api.getCustomers(); setCustomers(data); } catch {}
  };

  useEffect(() => { loadPayments(); loadCustomers(); }, [filter]); // eslint-disable-line

  const loadSquareTxns = async () => {
    try { const data = await api.request('/api/payments/square/transactions'); setSquareTxns(data); } catch {}
  };

  const handleNew = () => {
    setEditingId(null);
    setForm({ ...emptyPayment });
    setShowForm(true);
  };

  const handleEdit = (p) => {
    setEditingId(p.id);
    setForm({
      customer_name: p.customer_name, sales_order_number: p.sales_order_number,
      payment_type: p.payment_type, amount: p.amount, check_number: p.check_number || '',
      check_date: p.check_date ? p.check_date.split('T')[0] : '', check_front_url: p.check_front_url || '',
      check_back_url: p.check_back_url || '',
      square_transaction_id: p.square_transaction_id || '', notes: p.notes || '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.customer_name) { alert('Select a customer'); return; }
    if (!form.sales_order_number.trim()) { alert('Sales order number is required — payment must reference an existing sales order.'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { alert('Enter a valid payment amount'); return; }
    if (form.payment_type === 'check') {
      if (!form.check_number?.trim()) { alert('Check number is required for check payments'); return; }
      if (!form.check_date?.trim()) { alert('Check date is required for check payments'); return; }
    }

    // If we have order info, validate amount against the BALANCE DUE (not invoice total)
    // and surface a confirmation popup if it doesn't match.
    if (!editingId && orderInfo) {
      const enteredAmt = parseFloat(form.amount);
      const balanceDue = orderInfo.balance_due;
      const invoiceTotal = orderInfo.invoice_total;
      const matchesBalance = Math.abs(enteredAmt - balanceDue) < 0.01;
      const matchesInvoice = Math.abs(enteredAmt - invoiceTotal) < 0.01;

      if (!matchesBalance && !matchesInvoice) {
        const remaining = (balanceDue - enteredAmt).toFixed(2);
        const isPartial = enteredAmt < balanceDue;
        const isOver = enteredAmt > balanceDue;
        const msg = `⚠️ AMOUNT MISMATCH

Sales Order: ${orderInfo.order_number}
Invoice Total: $${invoiceTotal.toFixed(2)}
Already Paid: $${orderInfo.amount_paid.toFixed(2)}
Balance Due: $${balanceDue.toFixed(2)}
Payment Entered: $${enteredAmt.toFixed(2)}

${isPartial ? `This is a PARTIAL PAYMENT. Remaining balance after this payment will be $${remaining}.` : ''}${isOver ? `This OVERPAYS by $${Math.abs(parseFloat(remaining)).toFixed(2)}.` : ''}

Continue and credit this amount to the customer's account statement?`;
        if (!window.confirm(msg)) return;
      }
    }

    setSaving(true);
    try {
      let result;
      if (editingId) {
        result = await api.request(`/api/payments/${editingId}`, { method: 'PUT', body: JSON.stringify(form) });
      } else {
        result = await api.request('/api/payments', { method: 'POST', body: JSON.stringify(form) });
        if (result.warning) {
          alert(result.warning);
        } else if (result.sales_order) {
          alert(`Payment recorded.\nInvoice: $${result.sales_order.invoice_total.toFixed(2)}\nTotal Paid: $${result.sales_order.amount_paid.toFixed(2)}\nBalance Due: $${result.sales_order.balance_due.toFixed(2)}\nStatus: ${result.sales_order.payment_status.toUpperCase()}`);
        }
      }
      setShowForm(false);
      setEditingId(null);
      setOrderInfo(null);
      setOrderLookupErr('');
      loadPayments();
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this payment? The linked sales order payment status will be recalculated.')) return;
    try {
      await api.request(`/api/payments/${id}`, { method: 'DELETE' });
      loadPayments();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const applySquareTxn = (txn) => {
    setForm(prev => ({
      ...prev,
      payment_type: 'square',
      amount: txn.amount,
      square_transaction_id: txn.id,
      notes: `Square: ${txn.card_brand || ''} ****${txn.last_4 || ''} ${txn.status || ''}`.trim(),
    }));
  };

  const filtered = payments.filter(p =>
    !search || p.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.sales_order_number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Payments Received</h2>
          <p className="text-muted-foreground mt-1">Track cash, check, and Square credit card payments</p>
        </div>
        <button onClick={handleNew} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90" data-testid="new-payment-btn">
          <Plus className="h-4 w-4" /> Record Payment
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 items-center">
        {[{ key: 'all', label: 'All Payments' }, ...PAYMENT_TYPES].map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${filter === t.key ? 'bg-primary text-white' : 'bg-muted hover:bg-muted/80'}`}
            data-testid={`filter-${t.key}`}>
            {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search customer or SO#..." className="pl-9 pr-3 py-2 border border-input rounded-md text-sm w-64" />
        </div>
      </div>

      {/* New/Edit Form */}
      {showForm && (
        <div className="bg-card border border-border rounded-lg p-5 space-y-4" data-testid="payment-form">
          <h3 className="font-semibold">{editingId ? 'Edit Payment' : 'Record New Payment'}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Customer *</label>
              <select value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="payment-customer">
                <option value="">-- Select Customer --</option>
                {customers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Sales Order # *</label>
              <div className="relative">
                <input
                  type="text"
                  value={form.sales_order_number}
                  onChange={e => { setForm({...form, sales_order_number: e.target.value}); setOrderInfo(null); setShowSoSuggestions(true); }}
                  onFocus={() => setShowSoSuggestions(true)}
                  onBlur={(e) => { setTimeout(() => setShowSoSuggestions(false), 200); lookupOrder(e.target.value); }}
                  placeholder="Start typing SO #..."
                  className="w-full px-3 py-2 border border-input rounded-md text-sm"
                  autoComplete="off"
                  data-testid="payment-so-number"
                />
                {showSoSuggestions && soSuggestions.length > 0 && !orderInfo && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg z-50 max-h-64 overflow-y-auto" data-testid="payment-so-suggestions">
                    {soSuggestions.map((o) => (
                      <button
                        key={o.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setForm(f => ({ ...f, sales_order_number: o.order_number, customer_name: o.customer_name || f.customer_name }));
                          setShowSoSuggestions(false);
                          setTimeout(() => lookupOrder(o.order_number), 0);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-muted text-sm border-b border-border last:border-b-0"
                        data-testid={`payment-so-suggestion-${o.order_number}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="font-mono font-semibold">{o.order_number}</div>
                            <div className="text-xs text-muted-foreground">{o.customer_name}</div>
                          </div>
                          <div className="text-xs text-muted-foreground whitespace-nowrap">
                            {o.order_date ? new Date(o.order_date).toLocaleDateString() : ''}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {orderLookupLoading && <p className="text-xs text-muted-foreground mt-1">Looking up sales order…</p>}
              {orderLookupErr && (
                <p className="text-xs text-red-600 mt-1" data-testid="so-lookup-error">{orderLookupErr}</p>
              )}
              {orderInfo && (
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs space-y-0.5" data-testid="so-info">
                  <div className="font-medium text-blue-900">{orderInfo.customer_name}</div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Invoice Total:</span> <span className="font-medium">${orderInfo.invoice_total.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Already Paid:</span> <span>${orderInfo.amount_paid.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold"><span>Balance Due:</span> <span className={orderInfo.balance_due > 0.01 ? 'text-red-600' : 'text-green-600'}>${orderInfo.balance_due.toFixed(2)}</span></div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Status: {orderInfo.payment_status}</div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Amount *</label>
              <div className="flex gap-2">
                <input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}
                  placeholder="0.00" className="flex-1 px-3 py-2 border border-input rounded-md text-sm" data-testid="payment-amount" />
                {orderInfo && orderInfo.balance_due > 0.01 && (
                  <button
                    type="button"
                    onClick={() => setForm({...form, amount: orderInfo.balance_due.toFixed(2)})}
                    className="px-3 py-2 text-xs border border-input rounded-md hover:bg-muted"
                    title="Pay full balance"
                    data-testid="pay-full-btn"
                  >
                    Pay Full ${orderInfo.balance_due.toFixed(2)}
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Payment Type *</label>
              <div className="flex gap-2">
                {PAYMENT_TYPES.map(t => (
                  <label key={t.key} className={`flex items-center gap-1.5 px-3 py-2 rounded-md border cursor-pointer text-sm ${form.payment_type === t.key ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-input'}`}>
                    <input type="radio" name="ptype" value={t.key} checked={form.payment_type === t.key}
                      onChange={() => { setForm({...form, payment_type: t.key}); if (t.key === 'square') loadSquareTxns(); }} className="sr-only" />
                    <t.icon className="h-4 w-4" /> {t.label}
                  </label>
                ))}
              </div>
            </div>
            {form.payment_type === 'check' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Check Number *</label>
                  <input type="text" value={form.check_number} onChange={e => setForm({...form, check_number: e.target.value})}
                    className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="payment-check-number" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Check Date *</label>
                  <input type="date" value={form.check_date} onChange={e => setForm({...form, check_date: e.target.value})}
                    className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="payment-check-date" />
                </div>
                <div className="col-span-full">
                  <label className="block text-xs font-medium text-muted-foreground mb-2">Scan Check Images (optional)</label>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">Front of Check</p>
                      <input type="file" accept="image/*" capture="environment"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = () => setForm(prev => ({...prev, check_front_url: reader.result}));
                          reader.readAsDataURL(file);
                        }}
                        className="w-full text-sm border border-input rounded-md p-1" data-testid="check-front-upload" />
                      {form.check_front_url && <img src={form.check_front_url} alt="Front" className="mt-1 h-16 rounded border" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">Back of Check</p>
                      <input type="file" accept="image/*" capture="environment"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const reader = new FileReader();
                          reader.onload = () => setForm(prev => ({...prev, check_back_url: reader.result}));
                          reader.readAsDataURL(file);
                        }}
                        className="w-full text-sm border border-input rounded-md p-1" data-testid="check-back-upload" />
                      {form.check_back_url && <img src={form.check_back_url} alt="Back" className="mt-1 h-16 rounded border" />}
                    </div>
                  </div>
                </div>
              </>
            )}
            {form.payment_type === 'square' && (
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Square Transaction ID</label>
                <input type="text" value={form.square_transaction_id} onChange={e => setForm({...form, square_transaction_id: e.target.value})}
                  className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="payment-square-id" />
              </div>
            )}
            <div className="col-span-full">
              <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
              <input type="text" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                placeholder="Optional notes..." className="w-full px-3 py-2 border border-input rounded-md text-sm" />
            </div>
          </div>

          {/* Square transactions dropdown */}
          {form.payment_type === 'square' && squareTxns.length > 0 && (
            <div className="border-t pt-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Recent Square Transactions (click to apply)</p>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {squareTxns.map(txn => (
                  <button key={txn.id} onClick={() => applySquareTxn(txn)}
                    className="w-full text-left px-3 py-2 text-sm border rounded-md hover:bg-muted/50 flex justify-between">
                    <span>{txn.card_brand} ****{txn.last_4} — {fmtDate(txn.created_at)}</span>
                    <span className="font-medium">{fmtCurrency(txn.amount)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50" data-testid="save-payment-btn">
              <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Payment'}
            </button>
            <button onClick={() => { setShowForm(false); setEditingId(null); }}
              className="flex items-center gap-2 px-4 py-2 border rounded-md hover:bg-muted">
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Payments Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/30">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Date</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Customer</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">SO #</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Type</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Details</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Amount</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Match</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-muted-foreground">No payments found</td></tr>
            ) : filtered.map(p => {
              const typeInfo = PAYMENT_TYPES.find(t => t.key === p.payment_type) || PAYMENT_TYPES[0];
              return (
                <tr key={p.id} className="hover:bg-muted/20">
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(p.created_at)}</td>
                  <td className="px-4 py-3 font-medium">{p.customer_name}</td>
                  <td className="px-4 py-3 font-medium text-primary">{p.sales_order_number}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${typeInfo.color}`}>{typeInfo.label}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {p.payment_type === 'check' && p.check_number && `Check #${p.check_number}`}
                    {p.payment_type === 'check' && p.check_date && ` (${fmtDate(p.check_date)})`}
                    {p.payment_type === 'square' && p.square_transaction_id && `Txn: ${p.square_transaction_id.slice(0, 12)}...`}
                    {p.payment_type === 'cash' && (p.notes || 'Cash')}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmtCurrency(p.amount)}</td>
                  <td className="px-4 py-3 text-center">
                    {p.amount_matches === false ? (
                      <span className="text-xs text-amber-600 font-medium">Partial</span>
                    ) : (
                      <span className="text-xs text-green-600 font-medium">Full</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleEdit(p)} className="p-1.5 hover:bg-muted rounded-md" data-testid={`edit-payment-${p.id}`}>
                        <Edit2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                      <button onClick={() => handleDelete(p.id)} className="p-1.5 hover:bg-red-50 rounded-md" data-testid={`delete-payment-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
